
import { Vue, Component, Prop, PropSync } from "vue-property-decorator";
import { Company, User } from "@/generated/graphql";
import CompanyContractorProvider from "@/components/companyContractors/CompanyContractorProvider.vue";
import AddContractorToCompany from "@/components/contractors/AddContractorToCompany.vue";
import BadgeColor from "@/entities/BadgeColor";
import Badge from "@/components/common/BadgeNew.vue";
import StatusChip from "@/components/icons/StatusChip.vue";
import ManagerAccessActions from "@/components/managers/ManagerAccessActions.vue";
import ManagerAccessBadges from "@/components/managers/ManagerAccessBadges.vue";
import getFullName from "@/helpers/getFullName";
import getInitials from "@/helpers/getInitials";
import { Tag } from "@/generated/graphql";
import getCountryCode from "@/utils/getCountryCode";
import RouteParams from "@/entities/RouteParams";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import ChatIcon from "@/components/icons/ChatIconNew.vue";
import ChipStatus from "@/entities/ChipStatus";
import getLocationInfo from "@/helpers/locations/getLocationInfo";
import EmployeeTag from "@/components/employee/EmployeeTagNew.vue";
import ChatIconHover from "@/components/icons/ChatIconHoverNew.vue";
import RouteNames from "@/entities/RouteNames";
import ResourceCards from "@/components/card/ResourceCards.vue";
import ViewIconHover from "@/components/icons/eyeHover.vue";
import ViewIcon from "@/components/icons/eye.vue";
import CountryFlag from "vue-country-flag";


interface ContractorUser {
  contracts: Contract[];
  [key: string]: any;
}

interface Contract {
  id: string;
  deleted?: boolean;
  managers: Manager[];
}

interface Manager {
  id: string;
  approved: boolean;
}

@Component({
  components: {
    ManagerAccessBadges,
    ResourceCards,
    ManagerAccessActions,
    StatusChip,
    AddContractorToCompany,
    ViewIconHover,
    ViewIcon,
    CompanyContractorProvider,
    Badge,
    ResourceAvatar,
    ChatIcon,
    ChatIconHover,
    EmployeeTag,
    CountryFlag
  },
})
export default class MarketplaceManagerCard extends Vue {
  @Prop({ type: Object, required: true }) readonly marketplaceUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @PropSync("nameBlockMinHeight", { type: Number, default: undefined })
  nameBlockMinHeightValue?: number;
  @PropSync("skillsBlockMinHeight", { type: Number, default: undefined })
  skillsBlockMinHeightValue?: number;

  requestSent: boolean | null = null;
  BadgeColor: typeof BadgeColor = BadgeColor;
  isHovered: boolean = false;
  isHoveredView: boolean = false;

  get contractorFullName(): string {
    return `${this.marketplaceUser.given_name} ${this.marketplaceUser.family_name}`;
  }

  get fullName(): string {
    return getFullName(
      this.marketplaceUser.given_name,
      this.marketplaceUser.family_name
    );
  }

  message(): void {
    this.$router.push({
      name: "inbox",
      query: {
        recipientUserId: this.marketplaceUser.id,
      },
    });
  }

  get managerInitials(): string {
    return `${this.marketplaceUser.given_name.trim()[0]}${
      this.marketplaceUser.family_name.trim()[0]
    }`;
  }

  get initials(): string {
    return getInitials(
      this.marketplaceUser.given_name,
      this.marketplaceUser.family_name
    );
  }

  get managerLocation(): string {
    return getLocationInfo(this.marketplaceUser.profile!);
  }

  get capitalizedManagerLocation(): string {
    if (!this.managerLocation) return "n/a";
    return (
      this.managerLocation.charAt(0).toUpperCase() +
      this.managerLocation.slice(1)
    );
  }

  get managerAvatar(): string | undefined {
    return this.marketplaceUser.avatar || undefined;
  }

  get country() {
    const userCountry =
      this.marketplaceUser.profile.country &&
      this.marketplaceUser.profile.country;
    return getCountryCode(userCountry);
  }

  get status(): ChipStatus {
    if (this.marketplaceUser.available) {
      return ChipStatus.ATTENTION;
    }

    return ChipStatus.INFO;
  }

  get skills() {
    const skills = this.marketplaceUser.profile.skills;
    return skills;
  }

  get contractorMinRate(): string | undefined {
    const minRate = this.marketplaceUser.profile.min_rate;

    if (!minRate) {
      return "n/a";
    }

    return `$${minRate}`;
  }

  get contractorMaxRate(): string | undefined {
    const maxRate = this.marketplaceUser.profile.max_rate;
    const minRate = this.marketplaceUser.profile.min_rate;
    const rateUnit = this.marketplaceUser.profile.rate_unit;

    if (!maxRate) {
      return ``;
    }
    if (!maxRate && minRate) {
      return `${rateUnit}`;
    }
    return `-$${maxRate}`;
  }

  get appliedTag(): Tag | undefined {
    return this.marketplaceUser?.contracts[0]?.tag || undefined;
  }

  get contractorRateUnit(): string | undefined {
    const maxRate = this.marketplaceUser.profile.max_rate;
    const minRate = this.marketplaceUser.profile.min_rate;
    const rateUnit = this.marketplaceUser.profile.rate_unit;

    if (!minRate && !maxRate && !rateUnit) {
      return "";
    }

    return `/${rateUnit}`;
  }

  get userJobTitle(): string {
    const jobTitle = this.marketplaceUser.profile.job_title;
    const companyName = this.marketplaceUser.profile.company_name;

    if (jobTitle || companyName) {
      // Construct the job title and company name string
      let titleString = jobTitle || "";
      if (companyName) {
        titleString += titleString ? ` at ${companyName}` : companyName;
      }
      return titleString.trim(); // Trim any leading/trailing spaces
    } else {
      return "n/a"; // Return 'n/a' if both job title and company name are null or empty
    }
  }

  get publicPageRouteParams(): RouteParams<{ id: string }> {
     if(this.isAccessGranted){
      return {
			name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
			params: {
				id: this.marketplaceUser.contracts[0]?.id,
			},
		};
    }
    else{
    return {
      name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
      params: {
        id: this.marketplaceUser.id,
      },
    };
    }
  }

  get contractorId(): string | undefined {
    return this.marketplaceUser?.contracts[0]?.id;
  }

  get isRequestContractorShown(): boolean {
    return !this.isAccessGranted && !this.isContractorRequested;
  }

  get isCompanyMember(): boolean {
    return this.marketplaceUser!.contracts.length > 0;
  }

  get contract(): Contract | undefined {
    return this.marketplaceUser!.contracts[0];
  }

  get managerContractor(): Manager | undefined {
    const managers = this.contract?.managers;

    if (!managers || managers.length === 0) {
      return undefined;
    }

    return managers[0];
  }

  get isAccessGranted(): boolean {
    if (!this.isCompanyMember) {
      return false;
    }

    return Boolean(!this.contract?.deleted && this.managerContractor?.approved);
  }

  get isContractorRequested(): boolean {
    if (this.requestSent !== null) {
      return this.requestSent;
    }

    if (!this.isCompanyMember) {
      return false;
    }

    return this.managerContractor?.approved === false;
  }

  changeRequestStatus(status: boolean): void {
    this.requestSent = status;
  }
}
